import { path, Time } from 'utils'
import { z } from 'zod'

import { CropType, resolveCropKey, storedCustomCropSchema, TaskType } from '../ag-concepts'
import { OperationLevelPermission, PlatformWidePermission } from '../auth-claims/permissions'
import { defineApi } from '../http'
import { DataSource } from '../shared'

export const taskSchema = z.enum(TaskType)

export const streamSchema = z.object({
  season: z.number(),
  task: z.object({
    key: taskSchema,
    name: z.string()
  }),
  task_index: z.number(),
  crop: z.string(),
  /**
   * The exact grower-entered name for the crop when `crop` is `CropType.Other`.
   */
  customCrop: storedCustomCropSchema,
  id: z.string(),
  /**
   * In older software, this does not exist, so it will often be undefined.
   * In newer software, it is a user-configurable nickname for the pass, e.g.,
   * "Burndown" or "Replant."
   *
   * Historically, Panorama defaulted pass names to "Pass A", "Pass B", etc.,
   * but this wasn't entirely deterministic -- or at least it did not guarantee
   * that Pass A would remain Pass A forever, or that the user would understand
   * which pass was which if they were looking at data from multiple sources.
   */
  passName: z.string().optional(),
  /**
   * time in seconds since epoch
   * note: must multiply by 1000 to get milliseconds or to pass to Date constructor
   */
  last_modified_time: z.number().optional(),
  /**
   * The source of the stream data (e.g., Gen3, Gen2, etc.)
   */
  source: z.object({
    key: z.enum(DataSource),
    name: z.string()
  })
})

export const fieldSchema = z.object({
  client: z.string().transform((x) => x || '--'),
  farm: z.string().transform((x) => x || '--'),
  field: z.string().transform((x) => x || '--'),
  streams: z.array(streamSchema),
  uuid: z.string(),
  /**
   * time in seconds since epoch
   * note: must multiply by 1000 to get milliseconds or to pass to Date constructor
   */
  last_modified_time: z.number().optional()
})

export const dbmSchema = z.object({
  serial_number: z.number(),
  fields: z.array(fieldSchema)
})

export const fieldsResponseSchema = z.array(dbmSchema)

/**
 * Optional filters that can be applied to the fields api,
 * all filters are case sensitive
 */
const filtersSchema = z.object({
  client: z.string().optional(),
  farm: z.string().optional(),
  field: z.string().optional(),
  task: taskSchema.optional(),
  season: z.number().optional(),
  crop: z.enum(CropType).optional(),
  streamsSince: z.number().optional()
})

export const fieldAndPassCatalogQueryKey = 'field-and-pass-catalog'

export const fieldsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: filtersSchema,
  pathFor: ({ orgCode, ...filters }) => path(`/account/${orgCode}/fields`, filters),
  queryKey: ({ orgCode, ...filters }) => [fieldAndPassCatalogQueryKey, orgCode, filters],
  name: 'GetFieldAndPassCatalog',
  description:
    "Retrieve the operation's catalog of fields and the passes (streams) within them, grouped by field, across all seasons — identity only: field/farm/client name, streamId, crop, task, season, and pass name. It carries no measurements or timing (no acres, yield, moisture, rates, volumes, or finish times). Use it to discover what exists, count fields or passes, or resolve a name to a streamId (for example to open a field for analysis). It is a lookup, not an answer: once you find the field or pass here, follow up with the right tool — the summary tools for acres or yield, the recent-activity lookup for the latest passes, and the archive tool to archive a pass (treat a request to delete a field as archiving the pass(es) in it). Supports optional filters: client, farm, field, task, season, crop.",
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: fieldsResponseSchema,
  gatewayPath: '/:orgCode/fields' as const
})

/**
 * Get information about a specific stream, including its task, crop, season, and associated field.
 * This is not actually a "GetItem" call, but it effectively mimics it by taking a streamId as a parameter.
 */
export const getStreamInfoApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  pathFor: (p) => path(`/account/${p.orgCode}/fields`, { stream_id: p.streamId }),
  name: 'GetStreamInfo',
  description: 'Retrieve detailed information about a specific data stream',
  requiredPermissions: OperationLevelPermission.ViewApplicationData,
  responseSchema: fieldsResponseSchema
    .transform((dbms) => dbms.flatMap((dbm) => dbm.fields.flatMap((field) => field.streams)))
    .refine((streams) => streams.length === 1, {
      message: 'Expected exactly one stream'
    }),
  gatewayPath: '/:orgCode/fields' as const,
  querySchema: z.object({ streamId: z.string() })
})

export const plannedFieldsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string()
  }),
  querySchema: filtersSchema.pick({ field: true, farm: true, client: true }),
  pathFor: ({ orgCode, ...filters }) =>
    path(`/account/${orgCode}/fields`, { ...filters, type: 'planned' }),
  name: 'GetPlannedFields',
  description: 'Retrieve fields configured for planning (without stream data)',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: z.object({
    fields: z.array(fieldSchema.omit({ streams: true, last_modified_time: true }))
  }),
  gatewayPath: '/:orgCode/fields' as const
})

export const accountConfigToCffsAPI = defineApi({
  pathFor: () => '/account/import',
  requestBodySchema: z.instanceof(File).refine((file) => file.name.endsWith('.2020'), {
    message: 'File must be a .2020 account config file'
  }),
  method: 'POST',
  name: 'AccountConfigToCffsAPI',
  description: 'Takes a 2020 account config file and returns an array of CFF objects',
  requiredPermissions: PlatformWidePermission.PerformBasicUserActions,
  responseSchema: z.array(
    z.object({
      client: z.string(),
      farm: z.string(),
      field: z.string()
    })
  ),
  gatewayPath: '/import' as const
})

// TODO: reconsider the coupling with frontend logic here -- should the API care about "live" data?
// similarly, this returns an optional passName, not the guaranteed passName that the frontend needs
// (using the "Pass A", "Pass B" scheme, which is not derivable without knowledge of live streams as well --
// not to mention a translate function). For now, this is just a convenient place to put shared logic --
// in particular, the Time.fromSeconds because callers might not correctly interpret seconds/milliseconds here.
export const flattenStreams = (streams: FieldsResponse) =>
  streams.flatMap((dbm) =>
    dbm.fields.flatMap((field) =>
      field.streams.flatMap((stream) => ({
        streamId: stream.id,
        crop: stream.crop,
        customCrop: stream.customCrop,
        cropKey: resolveCropKey(stream),
        serialNumber: dbm.serial_number,
        season: stream.season,
        task: stream.task.key,
        taskLabel: stream.task.name,
        passName: stream.passName,
        client: field.client,
        farm: field.farm,
        field: field.field,
        uuid: field.uuid as string | undefined, // this is to allow live data without uuid to be interleaved with old data (and we don't want to rely much on uuid on the client anyway since it's not a panorama concept)
        taskIndex: stream.task_index,
        lastModified: Time.fromSeconds(stream.last_modified_time ?? 0), // default to epoch. This should only be undefined in old data (uploaded before Panorama was publicly released).
        isLive: false, // presume false until we know otherwise
        dataSource: stream.source.key // for indicating if data is from a Gen2 20|20 monitor
      }))
    )
  )

export type FieldsResponse = z.infer<typeof fieldsResponseSchema>

/**
 * @group Routes
 *
 * @route
 * `GET /{stitch_prefix}/account/{org_code}/fields?streamsSince={streams_since}`
 *
 * @apiParam {stitch_prefix} prefix to stitch
 * @apiParam {org_code} organization/operation id
 * @apiParam {streams_since} return changes since the provided epoch (optional - defaults to 0)
 */
export type Field = z.infer<typeof fieldSchema>

/**
 * A stream is the basic unit of organization for 2020 data.
 * All data generated by a 2020 is stored within the context of a stream,
 * and a stream is uniquely identified by the following attributes:
 * - dbm serial number
 * - season
 * - field uuid
 * - {@link Task}
 * - task index (a task index has a unique crop, but crop is not relevant in identifying the stream)
 *
 * Stitch field data routes always begin with `/streams/{orgCode}/{streamId}/...`
 *
 * @group Domain Concepts
 */
export type Stream = z.infer<typeof streamSchema>

/**
 *
 * A task is a type of activity in the field: planting, spraying, etc.
 *
 *  @group Domain Concepts
 */
export type Task = z.infer<typeof taskSchema>

export type DBM = z.infer<typeof dbmSchema>
